import { getCentralSystem } from '@infuse/api/e-connector/central-system';
import { linkChargingStation } from '@infuse/api/e-connector/charging-station';
import { useNotify } from '@infuse/cpms/ui';
import { LinkedCentralSystem } from '@infuse/e-connector/central-system/domain';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { createLinkRequestFor } from './useUpsertCentralSystemLink';

export const useCopyCentralSystemLinks = () => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyFailure } = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  return useMutation({
    mutationFn: async ({
      links,
      toChargingStationId
    }: {
      links: LinkedCentralSystem[];
      toChargingStationId: number;
    }) => {
      // the link API takes the central system name, but source links only carry ids
      const uniqueIds = [...new Set(links.map((link) => link.centralSystemId))];
      const nameResults = await Promise.allSettled(uniqueIds.map((id) => getCentralSystem({ id: id.toString() })));

      const namesById = new Map<number, string>();
      nameResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          namesById.set(uniqueIds[index], result.value.name);
        }
      });

      const resolvedLinks = links.filter((link) => namesById.has(link.centralSystemId));
      const results = await Promise.allSettled(
        resolvedLinks.map((link) =>
          linkChargingStation(
            { id: toChargingStationId },
            createLinkRequestFor(link, namesById.get(link.centralSystemId) as string, false)
          )
        )
      );

      const failed = [
        // central systems whose name could not be resolved (e.g. not accessible to the current user)
        ...links.filter((link) => !namesById.has(link.centralSystemId)).map((link) => `#${link.centralSystemId}`),
        ...resolvedLinks
          .filter((_, index) => results[index].status === 'rejected')
          .map((link) => namesById.get(link.centralSystemId) as string)
      ];

      return { failed };
    },
    onSuccess: async ({ failed }) => {
      // some links may have succeeded even when others failed, so always refetch
      await queryClient.invalidateQueries({ queryKey: ['charging-stations'] });

      if (failed.length === 0) {
        notifySuccess({
          title: t('e_connector.common.notify.success'),
          message: t('e_connector.chargingStationLinks.notify.copy.success')
        });
      } else {
        notifyFailure({
          title: t('e_connector.common.notify.error'),
          message: t('e_connector.chargingStationLinks.notify.copy.error', { names: failed.join(', ') })
        });
      }
    }
  });
};
